/**
 * The discount badge on a coupon card (src/components/CouponCard.tsx). Pure.
 *
 * Rule (2026-10-24): the badge never contradicts the card heading. The heading is what the shopper reads (the
 * description, else the title, exactly as the card renders it) and it is the merchant text, so:
 *  - a stored PERCENT / FIXED value is shown only when the heading states the same figure or states no figure of that
 *    kind; when the heading states other figures, the badge takes the heading's figure (hostinger e5e00be2: stored 50,
 *    heading "Get 10% off…" -> 10%);
 *  - with no stored value, the heading is read first and the title only after it: the title's figure is kept when the
 *    heading states it too (or states no percentage at all), otherwise the heading's figure wins (title "10% Off",
 *    heading "Get up to 85% off…" -> 85%);
 *  - amounts follow the same rule, but only an amount the heading states as a discount ("$20 off", "save $20") overrides
 *    another figure; prices in the heading ("for $59, reduced from $117") do not;
 *  - FREE SHIP / DEAL / FREE fallbacks; an offer with no figure is never a bare "%" (it is "DEAL", or "FREE" when the text says free).
 * Only a percentage phrased as a saving counts ("X% off", "save X%", "-X%"; not "20% commissions", "100% SSD",
 * "99.9% uptime"), as only an amount phrased as a discount does. When the heading states several saving percentages
 * and the badge must take one, it takes the headline one: the largest under 100 ("Save 25% plus an extra 75% off" ->
 * 75); 100 only when it is the sole figure.
 */
import { formatMoney, parseDiscountAmounts, parseDiscountPercents, parseMoney, parseMoneyAll, parsePercent, parsePercents, reconcileFixedValue, type MoneyAmount } from "./discount-parse";

export type CouponDiscountType = "PERCENT" | "FIXED" | "FREE_SHIPPING" | "DEAL" | null | undefined;

export interface CouponBadgeInput {
    title: string;
    description?: string | null;
    /** Coupon.type: "CODE" | "DEAL" (default "CODE", as on the card). */
    type?: string;
    discountType?: CouponDiscountType;
    discountValue?: number | null;
}

export interface CouponBadge {
    type: "percent" | "money" | "deal";
    value: string;
}

/** The card heading: the description, else the title (what <h3 className="sp-coupon__title"> renders). */
export function couponHeading(title: string, description?: string | null): string {
    return description || title;
}

/** The headline percentage of a list the heading states: the largest under 100, else 100 (null for an empty list). */
export function headlinePercent(percents: number[]): number | null {
    if (percents.length === 0) return null;
    const under = percents.filter(p => p < 100);
    return under.length ? Math.max(...under) : Math.max(...percents);
}

/**
 * Percentages. Only a percentage the text phrases as a saving ("10% off", "save 10%", "-10%") can become the badge:
 * "20% commissions", "100% SSD", "99.9% uptime" never do. `preferred` (the stored value, or the title's figure) is kept
 * when the heading states it at all or states no saving percentage; otherwise the heading's headline saving percentage
 * wins.
 */
function pickPercent(preferred: number | null, headingAll: number[], headingSavings: number[]): number | null {
    if (preferred !== null && (headingAll.includes(preferred) || headingSavings.length === 0)) return preferred;
    return headlinePercent(headingSavings);
}

/**
 * The title's percentage when the title phrases it as a saving ("10% Off", "Save 20%") or is only the figure
 * ("Up to 30%", "13-56%"); null for "Free Gift" or "20% more storage".
 */
function titlePercent(title: string): number | null {
    const savings = parseDiscountPercents(title);
    if (savings.length) return savings[0];
    return TITLE_FIGURE_ONLY.test(title) ? parsePercent(title) : null;
}

const TITLE_FIGURE_ONLY = /^\s*(?:up to\s+)?\d{1,3}(?:\.\d+)?\s*%?(?:\s*(?:[-–+&]|and|to)\s*\d{1,3}(?:\.\d+)?\s*%?)*\s*$/i;

/**
 * Amounts. Headings often state prices ("for $59, reduced from $117"), so only an amount the heading states as a
 * discount ("$20 off", "save $20") overrides another figure:
 *  - no amount in the heading, or no `preferred` amount: `preferred`, else the heading's first amount (as before);
 *  - the heading states the same amount: the heading's own spelling (€20 for a stored 20; $29.99 for a stored 30, the
 *    column is an integer);
 *  - otherwise the heading's first discount amount ("Get $20 off" beats a stored 50), else `preferred`.
 */
function pickMoney(preferred: MoneyAmount | null, heading: MoneyAmount[], headingDiscounts: MoneyAmount[]): MoneyAmount | null {
    if (heading.length === 0) return preferred;
    if (preferred === null) return heading[0];
    return heading.find(h => h.value === preferred.value)
        ?? heading.find(h => Math.round(h.value) === preferred.value)
        ?? headingDiscounts[0]
        ?? preferred;
}

export function getCouponBadge({ title, description, type = "CODE", discountType, discountValue }: CouponBadgeInput): CouponBadge {
    const textToSearch = `${title} ${description || ""}`;
    const heading = couponHeading(title, description);
    const headingPercents = parsePercents(heading);
    const headingSavings = parseDiscountPercents(heading);
    const headingMoney = parseMoneyAll(heading);
    const headingDiscounts = parseDiscountAmounts(heading);

    // Stored values (0 / null fall through to the text). A stored FIXED value cut at the thousands separator (1 for
    // "$1,000") is corrected from the text first (SEO R3 rule 8).
    if (discountType === "PERCENT" && discountValue != null && discountValue > 0) {
        return { type: "percent", value: `${pickPercent(discountValue, headingPercents, headingSavings)}%` };
    }
    if (discountType === "FIXED" && discountValue != null && discountValue > 0) {
        const stored = reconcileFixedValue(discountValue, textToSearch) ?? discountValue;
        // A FIXED value whose heading states only a saving percentage ("Save 20%…") is the heading's percentage.
        if (headingMoney.length === 0 && headingSavings.length > 0) return { type: "percent", value: `${headlinePercent(headingSavings)}%` };
        return { type: "money", value: formatMoney(pickMoney({ currency: "$", value: stored }, headingMoney, headingDiscounts)!) };
    }
    if (discountType === "FREE_SHIPPING") return { type: "deal", value: "FREE SHIP" };
    // A stored DEAL on a code still reads its figure from the text below; with none it ends as "DEAL"/"FREE", never "% OFF".
    if (discountType === "DEAL" && type === "DEAL") return { type: "deal", value: "DEAL" };

    // No stored value: the heading first, then the title. "30% Off", "Save 50%", "Up to 68%".
    const percent = pickPercent(titlePercent(title), headingPercents, headingSavings);
    if (percent) return { type: "percent", value: `${percent}%` };

    // Amounts keep their thousands: "$1,000 Back" -> "$1,000" (was "$1"), "€20" -> "€20".
    const money = pickMoney(parseMoney(title), headingMoney, headingDiscounts);
    if (money) return { type: "money", value: formatMoney(money) };

    // FREE offers: "free trial", "free gift", "free domain", etc.
    if (/\bfree\b/i.test(textToSearch)) return { type: "deal", value: "FREE" };

    // No figure at all: the non-numeric label ("DEAL"), never a bare "%" (audit 2026-10-25 finding 3: "% OFF Get a free gift").
    return { type: "deal", value: "DEAL" };
}
